import { resourceSummary } from '../../../engine/map/resourceUtils';
import type { Tile } from '../../../engine/hex/types';
import type { ContentBuilder } from '../../ui/ContentBuilder';
import { t } from '../../../i18n/translations';
import type { Language } from '../../../i18n/translations';
import type { HexMapState } from '../../../engine/render/renderer';
import {
    ACTION__TILE__ASSIGN_SCOUT_TARGET,
    ACTION__TILE__OPEN_SCOUT,
    ACTION__TILE__RETURN_TROOPS,
} from '../../../engine/input/actionIds';
import { getNearestSettlementId } from '../utils';

export function renderTileSummaryTab(
    builder: ContentBuilder,
    state: HexMapState,
    tile: Tile,
    lang: Language,
): void {
    const biomeLabel = t(lang, `biome.${tile.biome.type}`);
    const biomeText = biomeLabel === `biome.${tile.biome.type}` ? tile.biome.type : biomeLabel;

    if (state.scoutPlanningMissionId) {
        const mission = state.scoutingMissions.find(
            (entry) => entry.id === state.scoutPlanningMissionId,
        );
        const canAssign = mission
            ? mission.targetTileId !== tile.id && mission.status !== 'complete'
            : false;
        builder.section(t(lang, 'tile.scoutPlan'), (section) => {
            section.text(t(lang, 'tile.scoutPlanHint'), 'muted');
            section.button(
                `${ACTION__TILE__ASSIGN_SCOUT_TARGET}:${state.scoutPlanningMissionId}`,
                t(lang, 'tile.scoutPlanAction'),
                canAssign,
            );
        });
        return;
    }

    if (!state.discoveredTileIds.has(tile.id)) {
        builder.section(t(lang, 'tile.undiscovered'), (section) => {
            section.text(t(lang, 'tile.undiscoveredHint'), 'muted');
        });
        const settlementId = getNearestSettlementId(state, tile.position);
        if (!settlementId) return;
        builder.section(t(lang, 'tile.scout'), (section) => {
            section.button(
                `${ACTION__TILE__OPEN_SCOUT}:${settlementId}`,
                t(lang, 'tile.scoutAction'),
                tile.biome.type !== 'water',
            );
        });
        return;
    }

    builder.section(t(lang, 'tile.summary'), (section) => {
        section.text(t(lang, 'tile.id', tile.id));
        section.text(t(lang, 'tile.position', tile.position.q, tile.position.r, tile.position.s));
        section.text(t(lang, 'tile.biome', biomeText));
        section.text(t(lang, 'tile.resources', resourceSummary(tile, lang)));
    });

    const enemy = state.enemyForces[tile.id];
    if (enemy && !enemy.defeatedAt) {
        const total = enemy.units.reduce((sum, group) => sum + group.count, 0);
        builder.section(t(lang, 'tile.hostiles'), (section) => {
            section.text(t(lang, 'tile.hostileFaction', t(lang, `enemy.${enemy.faction}`)));
            section.text(t(lang, 'tile.hostileCount', total));
        });
    }

    const holdingMission = state.scoutingMissions.find(
        (mission) => mission.targetTileId === tile.id && mission.status === 'holding',
    );
    if (holdingMission) {
        builder.section(t(lang, 'tile.scouts'), (section) => {
            section.text(t(lang, 'tile.scoutsWaiting'));
            section.button(
                `${ACTION__TILE__RETURN_TROOPS}:${holdingMission.id}`,
                t(lang, 'tile.scoutsReturn'),
                true,
            );
        });
        return;
    }

    const settlementId = getNearestSettlementId(state, tile.position);
    if (!settlementId) return;
    builder.section(t(lang, 'tile.scout'), (section) => {
        section.button(
            `${ACTION__TILE__OPEN_SCOUT}:${settlementId}`,
            t(lang, 'tile.scoutAction'),
            tile.biome.type !== 'water',
        );
    });
}
